import axios from 'axios';
import { getGraphBuilderBaseUrl } from '../../utils/env';

const client = axios.create({
  baseURL: getGraphBuilderBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 120000,
});

client.interceptors.request.use((config) => {
  console.log(`[EDNL] ${config.method?.toUpperCase()} ${config.url}`);
  return config;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    console.error('[EDNL] API Error:', err.response?.data || err.message);
    return Promise.reject(err);
  }
);

// Sources
export const sourcesApi = {
  readiness: () => client.get('/api/ednl/sources/readiness').then((r) => r.data),
  list: (workspaceId) => client.get('/api/ednl/sources', { params: { workspace_id: workspaceId } }).then(r => r.data),
  get: (id) => client.get(`/api/ednl/sources/${id}`).then(r => r.data),
  create: (data) => client.post('/api/ednl/sources', data).then(r => r.data),
  delete: (id) => client.delete(`/api/ednl/sources/${id}`).then(r => r.data),
  /** Partial update — uses PUT (PATCH is blocked by some proxies; tags already use PUT on /gb). */
  updateSource: (id, data) => client.put(`/api/ednl/sources/${id}`, data).then((r) => r.data),
  introspect: (id, opts = {}) => client.post(`/api/ednl/sources/${id}/introspect`, opts).then(r => r.data),
  getSchema: (id) => client.get(`/api/ednl/sources/${id}/schema`).then(r => r.data),
  /**
   * Upsert a single semantic tag without touching the sibling tags. Used by
   * the per-column tag editor in the Sources -> View schema panel. The
   * backend force-sets ``is_user_confirmed=true`` and ``confidence=1.0``.
   */
  updateTag: (id, tag) => client.patch(`/api/ednl/sources/${id}/tag`, tag).then((r) => r.data),
  autoInfer: (id, opts = {}) => client.post(`/api/ednl/sources/${id}/tags/auto-infer`, opts).then(r => r.data),
};

// Ontology
export const ontologyApi = {
  synthesize: (data) => client.post('/api/ednl/ontology/synthesize', data).then(r => r.data),
  listVersions: (workspaceId) => client.get('/api/ednl/ontology/versions', { params: { workspace_id: workspaceId } }).then(r => r.data),
  getVersion: (id) => client.get(`/api/ednl/ontology/versions/${id}`).then(r => r.data),
  getGraph: (id) => client.get(`/api/ednl/ontology/versions/${id}/graph`).then(r => r.data),
  getGraphSourceDocument: (id) =>
    client.get(`/api/ednl/ontology/versions/${id}/graph-source-document`).then((r) => r.data),
  publishToSNN: (id, body) =>
    client.post(`/api/ednl/ontology/versions/${id}/publish-to-runtime`, body).then((r) => r.data),
  diff: (oldId, newId) => client.get('/api/ednl/ontology/diff', { params: { old_version_id: oldId, new_version_id: newId } }).then(r => r.data),
  getWorkspaceOntologyGraph: (workspaceId) =>
    client.get(`/api/ednl/ontology/workspace/${workspaceId}/ontology-graph`).then((r) => r.data),
  setWorkspaceOntologyGraph: (workspaceId, body) =>
    client.put(`/api/ednl/ontology/workspace/${workspaceId}/ontology-graph`, body).then((r) => r.data),
  clearWorkspaceOntologyGraph: (workspaceId) =>
    client.delete(`/api/ednl/ontology/workspace/${workspaceId}/ontology-graph`).then((r) => r.data),
  materialize: (versionId, body) =>
    client.post(`/api/ednl/ontology/versions/${versionId}/materialize`, body).then((r) => r.data),
  getMaterializationRun: (runId) =>
    client.get(`/api/ednl/ontology/materialize-runs/${runId}`).then((r) => r.data),
  getValidationReport: (runId) =>
    client.get(`/api/ednl/ontology/materialize-runs/${runId}/validation-report`).then((r) => r.data),
  getLatestMaterialization: (workspaceId) =>
    client.get(`/api/ednl/ontology/workspace/${workspaceId}/latest-materialization`).then((r) => r.data),
  listMaterializationRuns: (workspaceId, limit = 50) =>
    client
      .get(`/api/ednl/ontology/workspace/${workspaceId}/materialization-runs`, { params: { limit } })
      .then((r) => r.data),
};

// Baselines
export const baselinesApi = {
  get: (entityType, entityId, sourceId = '') => client.get(`/api/ednl/baselines/${entityType}/${entityId}`, { params: { source_id: sourceId } }).then(r => r.data),
  bootstrap: (sourceId, entityType, metricName, windowDays = 90) => client.post(`/api/ednl/baselines/bootstrap/${sourceId}`, null, { params: { entity_type: entityType, metric_name: metricName, window_days: windowDays } }).then(r => r.data),
};

// Templates
export const templatesApi = {
  list: (workspaceId) => client.get('/api/ednl/templates', { params: { workspace_id: workspaceId } }).then(r => r.data),
  get: (id) => client.get(`/api/ednl/templates/${id}`).then(r => r.data),
  create: (data) => client.post('/api/ednl/templates', data).then(r => r.data),
  update: (id, data) => client.put(`/api/ednl/templates/${id}`, data).then(r => r.data),
  delete: (id) => client.delete(`/api/ednl/templates/${id}`).then(r => r.data),
  deleteAll: (workspaceId) => client.delete('/api/ednl/templates', { params: { workspace_id: workspaceId } }).then(r => r.data),
  autoGenerate: (workspaceId, versionId) => client.post(`/api/ednl/templates/auto-generate?workspace_id=${workspaceId}&version_id=${versionId}`).then(r => r.data),
};

// Patterns
export const patternsApi = {
  list: (workspaceId) => client.get('/api/ednl/patterns', { params: { workspace_id: workspaceId } }).then(r => r.data),
  get: (id) => client.get(`/api/ednl/patterns/${id}`).then(r => r.data),
  create: (data) => client.post('/api/ednl/patterns', data).then(r => r.data),
  update: (id, data) => client.put(`/api/ednl/patterns/${id}`, data).then(r => r.data),
  delete: (id) => client.delete(`/api/ednl/patterns/${id}`).then(r => r.data),
  activeWindows: (id) => client.get(`/api/ednl/patterns/${id}/active-windows`).then(r => r.data),
  test: (data) => client.post('/api/ednl/patterns/test', data).then(r => r.data),
};

// Frames
export const framesApi = {
  list: (workspaceId) => client.get('/api/ednl/frames', { params: { workspace_id: workspaceId } }).then(r => r.data),
  get: (id) => client.get(`/api/ednl/frames/${id}`).then(r => r.data),
  close: (id) => client.post(`/api/ednl/frames/${id}/close`).then(r => r.data),
  listRules: (workspaceId) => client.get('/api/ednl/frames/rules', { params: { workspace_id: workspaceId } }).then(r => r.data),
  createRule: (data) => client.post('/api/ednl/frames/rules', data).then(r => r.data),
  updateRule: (id, data) => client.put(`/api/ednl/frames/rules/${id}`, data).then(r => r.data),
  deleteRule: (id) => client.delete(`/api/ednl/frames/rules/${id}`).then(r => r.data),
};

// Bridge
export const bridgeApi = {
  listRouteMappings: (workspaceId) => client.get('/api/ednl/bridge/route-mappings', { params: { workspace_id: workspaceId } }).then(r => r.data),
  createRouteMapping: (data) => client.post('/api/ednl/bridge/route-mappings', data).then(r => r.data),
  updateRouteMapping: (id, data) => client.put(`/api/ednl/bridge/route-mappings/${id}`, data).then(r => r.data),
  deleteRouteMapping: (id) => client.delete(`/api/ednl/bridge/route-mappings/${id}`).then(r => r.data),
  listFeedbackRoutes: (workspaceId) => client.get('/api/ednl/bridge/feedback-routes', { params: { workspace_id: workspaceId } }).then(r => r.data),
  createFeedbackRoute: (data) => client.post('/api/ednl/bridge/feedback-routes', data).then(r => r.data),
  updateFeedbackRoute: (id, data) => client.put(`/api/ednl/bridge/feedback-routes/${id}`, data).then(r => r.data),
  deleteFeedbackRoute: (id) => client.delete(`/api/ednl/bridge/feedback-routes/${id}`).then(r => r.data),
};

// EDNL-Demo (Banking harness)
export const demoApi = {
  presets: () => client.get('/api/ednl/demo/presets').then((r) => r.data),
  config: () => client.get('/api/ednl/demo/config').then((r) => r.data),
  seed: (data) => client.post('/api/ednl/demo/seed', data).then((r) => r.data),
  run: (data) => client.post('/api/ednl/demo/run', data).then((r) => r.data),
  installPatterns: (data) =>
    client.post('/api/ednl/demo/patterns/install', data).then((r) => r.data),
  bootstrap: (data) =>
    client.post('/api/ednl/demo/bootstrap', data).then((r) => r.data),
  getExecutionStatus: (executionId) =>
    client.get(`/api/process/status/${executionId}`).then((r) => r.data),
};

// Audit
export const auditApi = {
  list: (workspaceId) => client.get('/api/ednl/audit', { params: { workspace_id: workspaceId } }).then(r => r.data),
  getProvenance: (executionId) => client.get(`/api/ednl/audit/${executionId}/provenance`).then(r => r.data),
};

// WebSocket URL helper
export function getWsNarrativeUrl() {
  const base = getGraphBuilderBaseUrl().replace(/^http/, 'ws');
  return `${base}/api/ednl/ws/narratives`;
}
